import axios from 'axios'
import { captureException } from '~/common/helpers'
import { withoutEmptyHeaders } from '~/common/http-headers'

/*
 * Global axios defaults for the REST endpoints (`/states`, `/cities`,
 * `/picklists`, `/company_configs`, ...).
 *
 * These used to live at module scope in `~/stores/fire.store`, which is why that
 * store — and with it the whole firebase SDK — had to be eagerly imported from
 * the stores barrel just so a couple of headers got set. They have nothing to do
 * with firebase, so they live on their own now and fire.store can be loaded when
 * something actually needs firestore.
 *
 * `baseURL` is re-assigned by `appStore.setApiUrl` once the tenant config lands;
 * this is the value to use before that, restored from the session.
 */
axios.defaults.baseURL = `${sessionStorage.getItem('apiUrl') || ''}/api/public/v1`
Object.assign(
	axios.defaults.headers,
	withoutEmptyHeaders({
		APP_ID: process.env.REACT_APP_PROXY_API_APP_ID,
		APP_SECRET: process.env.REACT_APP_PROXY_API_APP_SECRET,
	})
)

axios.interceptors.response.use(undefined, (error) => {
	captureException('Axios Client', error)
	return Promise.reject(error)
})

/*
 * Share one in-flight request between concurrent callers of the same GET.
 *
 * The picklist fetchers in master.store all guard on "do I already have the
 * result" — which is the right guard for a remount, and no guard at all for two
 * components mounting together. `/picklists?picklist_type=discipline` takes about
 * a second, so a second caller arriving inside that second still sees an empty
 * store and issues its own request. Captured on a signed-in staging session, a
 * single sign-in fired anywhere from two to four duplicate picklist requests, and
 * which ones varied per run — a timing race, not a fixed bug.
 *
 * Keyed by url + params and cleared when the request settles, so this only ever
 * collapses genuinely simultaneous calls; it is not a response cache, and a later
 * call still goes to the network.
 *
 * Note that everyone who joins an in-flight request gets the SAME response object.
 * All current callers read it, none mutate it.
 */
const inFlightGets = new Map()
const originalGet = axios.get.bind(axios)

axios.get = (url, config) => {
	const key = `${url}::${JSON.stringify(config?.params ?? null)}`

	const pending = inFlightGets.get(key)
	if (pending) {
		return pending
	}

	const request = originalGet(url, config)
	inFlightGets.set(key, request)

	const clear = () => inFlightGets.delete(key)
	request.then(clear, clear)

	return request
}
